import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, Box, Typography, Button, Alert} from '@mui/material';
import styles from '../../styles';
import {cancelShopifySubscription} from '../../api/membershipAPI';
import CancelMembershipReasonForm, {
  CancelMembershipReasonFormData,
  getCancelMembershipReasons,
} from './CancelMembershipReasonForm';

const CANCEL_MONTHLY_7_MONTH_MINIMUM_CUTOFF_DATE = '2026-02-12';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  quantity?: number;
  shopifySubscriptionId?: number;
  createdAt?: string;
  isMonthly?: boolean;
  onStart?: () => void;
  onSuccess?: () => Promise<void>;
  onError?: () => void;
}

const CancelMembershipModal: React.FC<Props> = ({
  open,
  setOpen,
  quantity,
  shopifySubscriptionId,
  createdAt,
  isMonthly,
  onStart,
  onSuccess,
  onError,
}) => {
  const {t} = useTranslation();

  const [pending, setPending] = useState<boolean>(false);
  const [step, setStep] = useState<'info' | 'reason'>('info');

  const handleCancelMembership = useCallback(
    async (data: CancelMembershipReasonFormData) => {
      const {reasons, additionalNotes} = getCancelMembershipReasons(data);

      if (!shopifySubscriptionId) {
        window.alert(t('membership.unknownSubscription'));
        return;
      }
      setPending(true);
      if (onStart) {
        onStart();
      }
      const res = await cancelShopifySubscription({
        shopifySubscriptionId,
        reasons,
        additionalNotes,
      });
      setPending(false);
      if (res) {
        setOpen(false);
        if (onSuccess) {
          await onSuccess();
        }
      } else {
        if (onError) {
          onError();
        }
      }
    },
    [shopifySubscriptionId, onStart, setOpen, onSuccess, onError, t],
  );

  const handleClose = useCallback(() => {
    setStep('info');
    setOpen(false);
  }, [setOpen]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={styles.modal}>
        <Typography id="modal-modal-title" variant="h4" sx={{mb: 2}}>
          {t('membership.cancelMembership')}
        </Typography>
        {step === 'info' && (
          <ul>
            {(
              t(
                isMonthly
                  ? createdAt &&
                    new Date(createdAt) <
                      new Date(CANCEL_MONTHLY_7_MONTH_MINIMUM_CUTOFF_DATE)
                    ? 'membership.cancelMembershipInstructionsMonthly'
                    : 'membership.cancelMembershipInstructionsMonthly20260219'
                  : 'membership.cancelMembershipInstructions',
                {
                  returnObjects: true,
                },
              ) as string[]
            ).map(instruction => (
              <li key={instruction}>
                <Typography variant="body1">{instruction}</Typography>
              </li>
            ))}
          </ul>
        )}
        {step === 'reason' && (
          <CancelMembershipReasonForm
            onSubmit={handleCancelMembership}
            onClose={handleClose}
            loading={pending}
          />
        )}
        {quantity && quantity > 1 && (
          <Alert severity="warning" variant="outlined" sx={{mb: 2}}>
            {t('membership.thisWillAffectAllSubscriptions', {
              quantity,
            })}
          </Alert>
        )}
        {step === 'info' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}>
            <Button
              variant="outlined"
              onClick={() => setStep('reason')}
              disabled={pending}>
              {t('general.continue')}
            </Button>
            <Button
              variant="contained"
              disabled={pending}
              onClick={handleClose}
              sx={{ml: 1}}>
              {t('general.close')}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default CancelMembershipModal;
